import React from 'react';
import { Box, Rating, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReviewViewModel } from '../../configuration/api/apiSchema';

interface RatingSummaryProps {
  reviews: ReviewViewModel[];
  loading: boolean;
  totalCount: number;
}

const RatingSummary: React.FC<RatingSummaryProps> = ({
  reviews,
  loading,
  totalCount,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Calculate average rating
  const averageRating = reviews.length
    ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) /
      reviews.length
    : 0;

  // Count ratings by star
  const ratingCounts = [0, 0, 0, 0, 0]; // 5 stars, 4 stars, 3 stars, 2 stars, 1 star
  reviews.forEach((review) => {
    const rating = Math.floor(review.rating || 0);
    if (rating > 0 && rating <= 5) {
      ratingCounts[5 - rating]++;
    }
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '24px 24px 32px',
          gap: '16px',
          background: isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
          borderRadius: '32px',
        }}
      >
        <Skeleton variant='text' width='60%' height={40} />
        <Skeleton variant='rectangular' width='100%' height={70} />
        <Skeleton variant='rectangular' width='100%' height={132} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '24px 24px 32px',
        gap: '16px',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow:
          '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
        borderRadius: '32px',
      }}
    >
      {/* Title and Rating Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '0px',
          gap: '8px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            fontFamily: 'Montserrat',
            fontStyle: 'normal',
            fontWeight: 800,
            fontSize: '20px',
            lineHeight: '160%',
            letterSpacing: '0.15px',
            color: isDark ? '#B6B6B6' : '#1A1A1A',
          }}
        >
          Rating Summary
        </Box>

        {/* Rating Display */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px',
            gap: '8px',
            width: '100%',
          }}
        >
          <Rating
            value={averageRating}
            readOnly
            precision={0.1}
            size='large'
            sx={{
              '& .MuiRating-iconFilled': {
                color: '#D1AB01',
              },
              '& .MuiRating-iconEmpty': {
                color: isDark ? '#444444' : '#D5D5D5',
              },
            }}
          />
          <Box
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '175%',
              letterSpacing: '0.15px',
              color: isDark ? '#B6B6B6' : '#1A1A1A',
            }}
          >
            {averageRating.toFixed(1)}
          </Box>
          <Box
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '143%',
              letterSpacing: '0.17px',
              color: isDark ? '#D5D5D5' : '#585858',
              flex: 1,
            }}
          >
            ({totalCount} {totalCount === 1 ? 'review' : 'reviews'})
          </Box>
        </Box>
      </Box>

      {/* Rating Breakdown */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          gap: '8px',
          width: '100%',
        }}
      >
        {[5, 4, 3, 2, 1].map((star, index) => {
          const count = ratingCounts[index];
          const percentage = totalCount ? (count / totalCount) * 100 : 0;

          return (
            <Box
              key={star}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '0px',
                gap: '8px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                  color: isDark ? '#D5D5D5' : '#585858',
                  width: '44px',
                }}
              >
                {star} {star === 1 ? 'star' : 'stars'}
              </Box>
              <Box sx={{ flex: 1, position: 'relative', height: '6px' }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '6px',
                    background: isDark ? '#1A1A1A' : '#E5E5DC',
                    boxShadow:
                      'inset 5px 5px 24px -11.7px rgba(0, 0, 0, 0.1), inset -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.04), inset 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.2), inset -2.82453px -2.82453px 8.49057px -5px rgba(255, 255, 255, 0.48)',
                    borderRadius: '64px',
                  }}
                />
                {percentage > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${percentage}%`,
                      height: '6px',
                      background: '#29A322',
                      boxShadow:
                        '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
                      borderRadius: '64px',
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '143%',
                  letterSpacing: '0.17px',
                  color: isDark ? '#D5D5D5' : '#585858',
                  minWidth: '20px',
                  textAlign: 'right',
                }}
              >
                {count}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default RatingSummary;
